import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { CustomFormControlAccessWithLoaders } from 'src/app/_core/tools/custom-form-control-access';
import { buildProviders } from '@kalidea/kaligraphi';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import {
    ArrayOfNotificationVup,
    MutationGetUserNotificationsVupArgs,
    NotificationVup, NotificationVupInput
} from 'src/graphql/generated-types';
import { BehaviorSubject, Observable, switchMap } from 'rxjs';
import gql from 'graphql-tag';
import { compare } from 'src/app/utils/array.utils';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { flatMap } from 'lodash-es';
import { tap } from 'rxjs/operators';
import uniqBy from 'lodash-es/uniqBy';

type NotificationFormGroupType = {
    Id: FormControl<number>;
    IdCategorie: FormControl<number>;
    Code: FormControl<string>;
    CodeCategorie: FormControl<string>;
    Libelle: FormControl<string>;
    IsChecked: FormControl<boolean>;
    Ordre: FormControl<number>;
    OrdreCategorie: FormControl<number>;
};

@Component({
    selector: 'app-shared-notification-settings',
    templateUrl: './shared-notification-settings.component.html',
    styleUrls: ['./shared-notification-settings.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(SharedNotificationSettingsComponent),
    standalone: false
})
export class SharedNotificationSettingsComponent extends CustomFormControlAccessWithLoaders implements OnInit {

    form: FormGroup<{ notificationCategoryList: FormArray }>;

    private categoryList: { label: string; code: string; }[] = [];

    private notificationList: NotificationVup[] = [];

    private _userIdSubject$: BehaviorSubject<number | null> = new BehaviorSubject(null);

    @Input()
    set userId(value: number | null) {
        this._userIdSubject$.next(value);
    }

    constructor(protected readonly cdr: ChangeDetectorRef,
                private readonly graphqlService: GraphqlService,
                private readonly fb: FormBuilder) {
        super(cdr);
    }

    get userId$(): Observable<number | null> {
        return this._userIdSubject$.asObservable();
    }

    get notificationCategoryListFormArray(): FormArray {
        return (this.form.get('notificationCategoryList') as FormArray);
    }

    getCategoryFromIndex(index: number): {
        CodeCategorie: string,
        LibelleCategorie: string,
        IsCategorieChecked: boolean,
        notificationList: NotificationVup[]
    } {
        return this.notificationCategoryListFormArray.controls[index].value;
    }

    private getNotificationSettingsList$(userId: number | null): Observable<ArrayOfNotificationVup> {
        return this.graphqlService.mutateOne<'GetUserNotificationsVUP', MutationGetUserNotificationsVupArgs>({
            mutation: gql`
                mutation GetUserNotificationsVUP($contexte: ContextInfosInput, $idUser: Float) {
                    GetUserNotificationsVUP(contexte: $contexte, idUser: $idUser) {
                        NotificationVUP {
                            Code
                            CodeCategorie
                            Id
                            IdCategorie
                            IsChecked
                            Libelle
                            LibelleCategorie
                            Ordre
                            OrdreCategorie
                        }
                    }
                }
            `,
            variables: {
                idUser: userId
            }
        });
    }

    private buildNotificationForm(): void {
        const filteredNotificationListByCategoryCode = (categoryCode: string): NotificationVup[] => this.notificationList.filter(n => n.CodeCategorie === categoryCode);

        this.categoryList.forEach(category => {
            const currentCategoryNotificationList = filteredNotificationListByCategoryCode(category.code).sort(compare('Ordre'));

            this.notificationCategoryListFormArray.push(
                this.fb.group({
                    CodeCategorie: category.code,
                    LibelleCategorie: category.label,
                    // Si au moins 1 notification de la catégorie est activée, alors la checkbox de la catégorie doit l'être aussi
                    IsCategorieChecked: currentCategoryNotificationList.some(n => !!n.IsChecked),
                    // Liste des notifications appartenant à cette catégorie.
                    notificationList: this.fb.array(
                        currentCategoryNotificationList.map(notification =>
                            this.fb.group<NotificationFormGroupType>({
                                Id: this.fb.control(notification.Id),
                                IdCategorie: this.fb.control(notification.IdCategorie),
                                Code: this.fb.control(notification.Code),
                                CodeCategorie: this.fb.control(notification.CodeCategorie),
                                Libelle: this.fb.control(notification.Libelle),
                                IsChecked: this.fb.control(notification.IsChecked),
                                Ordre: this.fb.control(notification.Ordre),
                                OrdreCategorie: this.fb.control(notification.OrdreCategorie)
                            })
                        )
                    )
                })
            );
        });

        this.notifyUpdate(this.normalize());
    }

    private normalize(): NotificationVupInput[] {
        const notificationFormValueList = flatMap(this.notificationCategoryListFormArray.value, 'notificationList');
        const categoryFormValueList = this.notificationCategoryListFormArray.value;

        return this.notificationList.map(notificationBase => {
            const formValue = notificationFormValueList.find(notificationFormValue => notificationFormValue.Id === notificationBase.Id);
            const isCategoryChecked = categoryFormValueList.find(c => c.CodeCategorie === notificationBase.CodeCategorie)?.IsCategorieChecked;

            return {
                ...notificationBase,
                ...formValue,
                IsChecked: !isCategoryChecked && !!formValue?.IsChecked ? false : formValue?.IsChecked // Si la catégorie est décochée, on décoche les enfants
            };
        });
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            notificationCategoryList: this.fb.array([])
        });

        this.watch$ = this.userId$.pipe(
            switchMap(userId => this.wrapLoader$(this.getNotificationSettingsList$(userId))),
            tap(notificationList => {
                this.notificationList = notificationList?.NotificationVUP || [];

                this.categoryList = uniqBy(this.notificationList, 'CodeCategorie')
                    .sort(compare('OrdreCategorie'))
                    .map(notification => ({label: notification.LibelleCategorie, code: notification.CodeCategorie}));

                this.buildNotificationForm();
            })
        );

        this.watch$ = this.form.valueChanges.pipe(
            tap(() => this.notifyUpdate(this.normalize()))
        );
    }

}
